import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  deleteDocument,
  getDocumentCountSummary,
  listAllStaffDocuments,
  listDocumentsByUser,
  uploadDocuments,
  listStaffDocumentCategories,
  updateDocumentExpiry,
  type DocumentCategory,
  type ListDocumentsParams,
  type ListAllStaffDocumentsParams,
} from '@/api/services/documents.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { usePermissions } from '@/hooks/usePermissions'

export const userDocumentsQueryKeys = {
  all: (userId: string) => ['users', 'documents', userId] as const,
  list: (userId: string, filters?: Omit<ListDocumentsParams, 'userId'>) =>
    ['users', 'documents', userId, filters] as const,
  summary: ['users', 'documents', 'summary'] as const,
  allStaff: (filters?: ListAllStaffDocumentsParams) =>
    ['users', 'documents', 'all-staff', filters] as const,
}

export function useUserDocuments(
  userId: string | null,
  filters?: Omit<ListDocumentsParams, 'userId'>,
  options?: { enabled?: boolean },
) {
  const { can, isLoading: permissionsLoading } = usePermissions()
  return useQuery({
    queryKey: userDocumentsQueryKeys.list(userId ?? '', filters),
    queryFn: () => listDocumentsByUser(userId!, filters),
    enabled:
      !!userId &&
      !permissionsLoading &&
      (can('documents', 'read_self') || can('documents', 'read')) &&
      options?.enabled !== false,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useStaffDocumentCategories(enabled = true) {
  return useQuery({
    queryKey: ['users', 'documents', 'categories'],
    queryFn: listStaffDocumentCategories,
    enabled,
    staleTime: 1 * 60 * 1000,
  })
}

export function useUploadDocuments(userId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      files,
      category,
      expiryDate,
      documentType,
    }: {
      files: File[]
      category: DocumentCategory
      expiryDate?: string
      documentType?: string
    }) => uploadDocuments(files, category, userId ?? undefined, expiryDate, documentType),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: userDocumentsQueryKeys.all(userId) })
      }
      queryClient.invalidateQueries({ queryKey: userDocumentsQueryKeys.summary })
      toast.success('Documents uploaded')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUpdateDocumentExpiry(userId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      expiryDate,
      documentType,
    }: {
      id: string
      expiryDate: string | null
      documentType?: string | null
    }) => updateDocumentExpiry(id, expiryDate, documentType),
    onSuccess: (_data, { documentType }) => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: userDocumentsQueryKeys.all(userId) })
      }
      // Expiry changes move documents in/out of the expiring/expired buckets.
      queryClient.invalidateQueries({ queryKey: userDocumentsQueryKeys.summary })
      toast.success(documentType === undefined ? 'Expiry date updated' : 'Document details updated')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDeleteDocument(userId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteDocument(id),
    onSuccess: () => {
      if (userId) {
        queryClient.invalidateQueries({ queryKey: userDocumentsQueryKeys.all(userId) })
      }
      queryClient.invalidateQueries({ queryKey: userDocumentsQueryKeys.summary })
      toast.success('Document deleted')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDocumentCountSummary(options?: { enabled?: boolean }) {
  const { can, isLoading: permissionsLoading } = usePermissions()
  return useQuery({
    queryKey: userDocumentsQueryKeys.summary,
    // API returns distinct logical document counts per user, not raw file totals.
    queryFn: getDocumentCountSummary,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !permissionsLoading && can('documents', 'read') && (options?.enabled !== false),
  })
}

export function useAllStaffDocuments(
  filters?: ListAllStaffDocumentsParams,
  options?: { enabled?: boolean },
) {
  const { can, isLoading: permissionsLoading } = usePermissions()
  return useQuery({
    queryKey: userDocumentsQueryKeys.allStaff(filters),
    queryFn: () => listAllStaffDocuments(filters),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled:
      !permissionsLoading &&
      can('documents', 'read_staff_others') &&
      options?.enabled !== false,
  })
}
